import { useState, useEffect } from 'react'
import { Modal, Form, Button, Table, Alert, Row, Col, Spinner } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { getDisplaySerieFolio } from '../../utils/invoiceDisplay'
import { formatDateOnlyEsMX, getTodayDateOnlyMx } from '../../utils/apiDateFormat'
import { formatCurrency } from '../../utils/formatters'
import {
  FORMA_PAGO_SAT_OPTIONS_PAGO,
  formaPagoRequiereCuentaBancaria,
  formaPagoToPaymentMethod,
} from '../../constants/formaPagoSat'

/** Redondea a 2 decimales para evitar errores de precisión flotante y validaciones del backend */
const roundMoney = (val) => Math.round((parseFloat(val) || 0) * 100) / 100

/** Formatea monto para mostrar (máx 12 dígitos incl. decimales: 9999999999.99) */
const formatMonto = (val) => formatCurrency(roundMoney(val))

/**
 * Modal para aplicar pagos de manera masiva a múltiples facturas o facturas recibidas.
 * Permite crear un nuevo pago o usar uno existente, aplicando el total de cada item (monto_pendiente).
 */
const BulkApplyPaymentModal = ({ show, onHide, items = [], isBill = false, onSuccess }) => {
  const queryClient = useQueryClient()
  const [selectedPayment, setSelectedPayment] = useState('')
  const [createNewPayment, setCreateNewPayment] = useState(false)
  const [newPaymentData, setNewPaymentData] = useState({
    payment_date: getTodayDateOnlyMx(),
    amount: 0,
    forma_pago: '01',
    payment_method: 'CASH',
    reference: '',
    bank_account: ''
  })

  // Filtrar items con saldo pendiente > 0 y calcular total (redondeado a 2 decimales)
  const itemsConPendiente = (items || []).filter(item => roundMoney(item.monto_pendiente || 0) > 0.01)
  const totalAPagar = roundMoney(itemsConPendiente.reduce((sum, item) => sum + roundMoney(item.monto_pendiente || 0), 0))
  const clienteId = itemsConPendiente[0] ? (typeof itemsConPendiente[0].client === 'object' ? itemsConPendiente[0].client?.id : (itemsConPendiente[0].client || itemsConPendiente[0].client_id)) : null
  const tipoPago = isBill ? 'egreso' : 'ingreso'

  // Verificar que todos pertenecen al mismo cliente
  const mismosClientes = itemsConPendiente.length <= 1 || itemsConPendiente.every(item => {
    const cid = typeof item.client === 'object' ? item.client?.id : (item.client || item.client_id)
    return cid === clienteId
  })

  const { data: bankAccounts } = useQuery(
    ['bank-accounts', clienteId],
    async () => {
      if (!clienteId) return []
      const response = await api.get('/financial/bank-accounts/', {
        params: { cliente_id: clienteId, is_active: true }
      })
      return response.data.results || response.data || []
    },
    { enabled: !!show && !!clienteId }
  )

  const { data: availablePayments, isLoading: loadingPayments } = useQuery(
    ['available-payments', clienteId, tipoPago],
    async () => {
      if (!clienteId) return []
      const response = await api.get('/financial/payments/', {
        params: {
          cliente_id: clienteId,
          estado: 'pendiente',
          tipo_pago: tipoPago
        }
      })
      const payments = response.data.results || response.data || []
      return payments.filter(p => parseFloat(p.monto_disponible || p.amount || 0) > 0.01)
    },
    { enabled: !!show && !!clienteId }
  )

  const buildAplicaciones = () => itemsConPendiente.map(item => ({
    ...(isBill ? { bill_id: parseInt(item.id, 10) } : { factura_id: parseInt(item.id, 10) }),
    monto: roundMoney(item.monto_pendiente || 0)
  }))

  const applyPaymentMutation = useMutation(
    async ({ paymentId }) => {
      return api.post(`/financial/payments/${paymentId}/aplicar/`, {
        aplicaciones: buildAplicaciones()
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('invoices')
        queryClient.invalidateQueries('bills')
        queryClient.invalidateQueries('payments')
        queryClient.invalidateQueries('available-payments')
        toast.success(`Pago aplicado a ${itemsConPendiente.length} ${isBill ? 'factura(s) recibida(s)' : 'factura(s)'}`)
        onSuccess?.()
        handleClose()
      },
      onError: (error) => {
        const errData = error.response?.data
        const msg = typeof errData?.error === 'string' ? errData.error : (errData?.detail || 'Error al aplicar pago')
        toast.error(msg)
      }
    }
  )

  const createAndApplyMutation = useMutation(
    async (paymentData) => {
      if (!clienteId) {
        throw new Error('No se pudo determinar el cliente. Verifique que las facturas tengan cliente asignado.')
      }
      if (formaPagoRequiereCuentaBancaria(paymentData.forma_pago) && !paymentData.bank_account) {
        throw new Error('La cuenta bancaria es requerida para transferencias y cheques')
      }
      const amount = roundMoney(paymentData.amount || 0)
      if (amount < 0.01) {
        throw new Error('El monto del pago debe ser mayor a 0')
      }
      // Enviar amount como número con exactamente 2 decimales para evitar errores de precisión
      const amountSafe = Number(amount.toFixed(2))
      const payload = {
        cliente: clienteId,
        payment_date: paymentData.payment_date,
        amount: amountSafe,
        forma_pago: paymentData.forma_pago,
        payment_method: formaPagoToPaymentMethod(paymentData.forma_pago),
        reference: (paymentData.reference || '').trim(),
        tipo_pago: tipoPago,
        estado: 'pendiente'
      }
      if (paymentData.bank_account) payload.bank_account = parseInt(paymentData.bank_account, 10)

      const createResponse = await api.post('/financial/payments/', payload)
      const payment = createResponse.data
      if (payment.id && totalAPagar > 0) {
        await api.post(`/financial/payments/${payment.id}/aplicar/`, {
          aplicaciones: buildAplicaciones()
        })
      }
      return payment
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('invoices')
        queryClient.invalidateQueries('bills')
        queryClient.invalidateQueries('payments')
        queryClient.invalidateQueries('available-payments')
        toast.success(`Pago creado y aplicado a ${itemsConPendiente.length} ${isBill ? 'factura(s) recibida(s)' : 'factura(s)'}`)
        onSuccess?.()
        handleClose()
      },
      onError: (error) => {
        const errData = error.response?.data
        let msg = 'Error al crear y aplicar pago'
        if (typeof errData?.error === 'string') msg = errData.error
        else if (typeof errData?.detail === 'string') msg = errData.detail
        else if (Array.isArray(errData?.detail)) msg = errData.detail.join(', ')
        else if (errData && typeof errData === 'object' && !Array.isArray(errData)) {
          const parts = Object.entries(errData).flatMap(([k, v]) =>
            Array.isArray(v) ? v.map(m => `${k}: ${m}`) : [`${k}: ${v}`]
          )
          if (parts.length) msg = parts.join('; ')
        }
        toast.error(msg)
      }
    }
  )

  useEffect(() => {
    if (itemsConPendiente.length > 0 && totalAPagar > 0) {
      setNewPaymentData(prev => ({ ...prev, amount: totalAPagar }))
    }
  }, [itemsConPendiente.length, totalAPagar])

  const handleClose = () => {
    setSelectedPayment('')
    setCreateNewPayment(false)
    setNewPaymentData({
      payment_date: getTodayDateOnlyMx(),
      amount: 0,
      forma_pago: '01',
      payment_method: 'CASH',
      reference: '',
      bank_account: ''
    })
    onHide()
  }

  const handleApplyPayment = () => {
    if (!selectedPayment) {
      toast.error('Selecciona un pago')
      return
    }
    const payment = availablePayments?.find(p => p.id === parseInt(selectedPayment))
    if (!payment) {
      toast.error('Pago no encontrado')
      return
    }
    const disponible = roundMoney(payment.monto_disponible || payment.amount || 0)
    if (totalAPagar > disponible) {
      toast.error(`El total a aplicar ($${totalAPagar.toFixed(2)}) excede el disponible ($${disponible.toFixed(2)})`)
      return
    }
    applyPaymentMutation.mutate({ paymentId: parseInt(selectedPayment) })
  }

  const handleCreateAndApply = () => {
    const montoPago = roundMoney(newPaymentData.amount || 0)
    if (montoPago < totalAPagar - 0.01) {
      toast.error(`El monto del pago ($${formatMonto(montoPago)}) debe ser al menos el total a aplicar ($${formatMonto(totalAPagar)})`)
      return
    }
    if (formaPagoRequiereCuentaBancaria(newPaymentData.forma_pago) && !newPaymentData.bank_account) {
      toast.error('La cuenta bancaria es requerida para transferencias y cheques')
      return
    }
    createAndApplyMutation.mutate(newPaymentData)
  }

  const isLoading = applyPaymentMutation.isLoading || createAndApplyMutation.isLoading

  if (itemsConPendiente.length === 0) return null

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Aplicar Pago Masivo - {itemsConPendiente.length} {isBill ? 'Factura(s) Recibida(s)' : 'Factura(s)'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!mismosClientes && (
          <Alert variant="danger">Las facturas seleccionadas pertenecen a diferentes clientes. Solo se pueden aplicar pagos masivos a facturas del mismo cliente.</Alert>
        )}

        <Alert variant="info" className="mb-3">
          <Row>
            <Col md={6}><strong>Total a aplicar:</strong> ${formatMonto(totalAPagar)}</Col>
            <Col md={6}><strong>Cantidad:</strong> {itemsConPendiente.length} documento(s)</Col>
          </Row>
        </Alert>

        <Table size="sm" responsive className="mb-3">
          <thead>
            <tr>
              <th>{isBill ? 'UUID' : 'Folio'}</th>
              <th>Total</th>
              <th>Pendiente</th>
            </tr>
          </thead>
          <tbody>
            {itemsConPendiente.map(item => (
              <tr key={item.id}>
                <td>
                  <strong>
                    {isBill ? (item.uuid?.slice(0, 12) + '...') : getDisplaySerieFolio(item.invoice_number)}
                  </strong>
                </td>
                <td>${formatMonto(item.total || 0)}</td>
                <td>${formatMonto(item.monto_pendiente || 0)}</td>
              </tr>
            ))}
          </tbody>
        </Table>

        <div className="mb-3">
          <div className="btn-group w-100" role="group">
            <Button variant={!createNewPayment ? 'primary' : 'outline-primary'} onClick={() => setCreateNewPayment(false)}>Usar Pago Existente</Button>
            <Button variant={createNewPayment ? 'primary' : 'outline-primary'} onClick={() => setCreateNewPayment(true)}>Crear Nuevo Pago</Button>
          </div>
        </div>

        {!createNewPayment ? (
          <div>
            {loadingPayments ? (
              <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
            ) : availablePayments?.length > 0 ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Seleccionar Pago (mínimo disponible: ${formatMonto(totalAPagar)})</Form.Label>
                  <Form.Select
                    value={selectedPayment}
                    onChange={e => setSelectedPayment(e.target.value)}
                  >
                    <option value="">Seleccionar pago...</option>
                    {availablePayments
                      .filter(p => roundMoney(p.monto_disponible || 0) >= totalAPagar - 0.01)
                      .map(p => (
                        <option key={p.id} value={p.id}>
                          Pago #{p.id} - ${formatMonto(p.amount || 0)} - Disponible: ${formatMonto(p.monto_disponible || 0)} - {formatDateOnlyEsMX(p.payment_date, {}, '—')}
                        </option>
                      ))}
                  </Form.Select>
                  {availablePayments.filter(p => roundMoney(p.monto_disponible || 0) >= totalAPagar - 0.01).length === 0 && availablePayments.length > 0 && (
                    <Form.Text className="text-warning">No hay pagos con saldo suficiente. Crea un nuevo pago.</Form.Text>
                  )}
                </Form.Group>
              </>
            ) : (
              <Alert variant="warning">No hay pagos disponibles para este cliente. Crea un nuevo pago.</Alert>
            )}
          </div>
        ) : (
          <div>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fecha de Pago *</Form.Label>
                  <Form.Control
                    type="date"
                    value={newPaymentData.payment_date}
                    onChange={e => setNewPaymentData({ ...newPaymentData, payment_date: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Forma de Pago *</Form.Label>
                  <Form.Select
                    value={newPaymentData.forma_pago}
                    onChange={e => {
                      const forma = e.target.value
                      setNewPaymentData({
                        ...newPaymentData,
                        forma_pago: forma,
                        payment_method: formaPagoToPaymentMethod(forma),
                        bank_account: formaPagoRequiereCuentaBancaria(forma) ? newPaymentData.bank_account : '',
                      })
                    }}
                    required
                  >
                    {FORMA_PAGO_SAT_OPTIONS_PAGO.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            {formaPagoRequiereCuentaBancaria(newPaymentData.forma_pago) && (
              <Form.Group className="mb-3">
                <Form.Label>Cuenta Bancaria *</Form.Label>
                <Form.Select
                  value={newPaymentData.bank_account || ''}
                  onChange={e => setNewPaymentData({ ...newPaymentData, bank_account: e.target.value })}
                  required
                >
                  <option value="">Seleccionar cuenta bancaria...</option>
                  {bankAccounts?.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name} - {acc.account_number} ({acc.bank_name || 'N/A'})</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Monto del Pago *</Form.Label>
                  <Form.Control
                    type="number"
                    min={totalAPagar}
                    step="0.01"
                    value={newPaymentData.amount === 0 ? '' : newPaymentData.amount}
                    onChange={e => setNewPaymentData({ ...newPaymentData, amount: roundMoney(e.target.value) || 0 })}
                    required
                  />
                  <Form.Text className="text-muted">Mínimo: ${formatMonto(totalAPagar)} (total a aplicar)</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Referencia</Form.Label>
                  <Form.Control
                    type="text"
                    value={newPaymentData.reference}
                    onChange={e => setNewPaymentData({ ...newPaymentData, reference: e.target.value })}
                    placeholder="Número de referencia, cheque, etc."
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>Cancelar</Button>
        {!createNewPayment ? (
          <Button
            variant="primary"
            onClick={handleApplyPayment}
            disabled={isLoading || !selectedPayment || !mismosClientes}
          >
            {isLoading ? <><Spinner animation="border" size="sm" className="me-2" /> Aplicando...</> : 'Aplicar Pago'}
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handleCreateAndApply}
            disabled={
              isLoading ||
              !newPaymentData.payment_date ||
              parseFloat(newPaymentData.amount || 0) < totalAPagar - 0.01 ||
              !mismosClientes ||
              (formaPagoRequiereCuentaBancaria(newPaymentData.forma_pago) && !newPaymentData.bank_account)
            }
          >
            {isLoading ? <><Spinner animation="border" size="sm" className="me-2" /> Creando y Aplicando...</> : 'Crear y Aplicar Pago'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export default BulkApplyPaymentModal
